import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Menu } from 'lucide-react';
import { Button } from '../ui/button';

const FloatingNavigationButton = ({ onOpenNavigation, defaultTop = 180, disabled = false }) => {
  const [position, setPosition] = useState({ top: defaultTop });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const buttonRef = useRef(null);
  const constraintsRef = useRef({
    minTop: 180,
    maxTop: window.innerHeight - 150
  });

  useEffect(() => {
    const updateConstraints = () => {
      constraintsRef.current = {
        minTop: 180,
        maxTop: window.innerHeight - 150
      };
    };

    window.addEventListener('resize', updateConstraints);
    return () => window.removeEventListener('resize', updateConstraints);
  }, []);

  const startDrag = useCallback((clientY) => {
    if (disabled || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setIsDragging(true);
    setDragOffset(clientY - rect.top);

    setTimeout(() => {
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }, 0);
  }, [disabled]);

  const updatePosition = useCallback(
    (clientY) => {
      if (!isDragging) return;

      const newTop = clientY - dragOffset;
      const { minTop, maxTop } = constraintsRef.current;

      setPosition({
        top: Math.max(minTop, Math.min(maxTop, newTop))
      });
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    setDragOffset(0);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isDragging]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      updatePosition(e.clientY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      updatePosition(e.touches[0].clientY);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseUp, updatePosition]);

  const handleClick = () => {
    if (!disabled && !isDragging) {
      onOpenNavigation();
    }
  };

  return (
    <div
      className="group fixed right-4 z-50 hidden transition-all duration-200 md:block"
      style={{
        top: `${position.top}px`,
        cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={handleClick}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          startDrag(e.clientY);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
          startDrag(e.touches[0].clientY);
        }}
        className={`h-12 w-12 rounded-full bg-primary p-0 text-white shadow-lg transition-all duration-200 hover:bg-primary/90 disabled:opacity-50 ${
          isDragging ? 'scale-105 shadow-xl ring-4 ring-primary/30' : 'hover:scale-105'
        }`}
        style={{
          cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
          touchAction: 'none'
        }}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {isDragging && (
        <div className="absolute -top-8 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white">
          Drag to reposition
        </div>
      )}
    </div>
  );
};

export default FloatingNavigationButton;
